import { BaseAPI } from './BaseAPI';
import { Proposal, AccAddress, Coins, Dec, Int } from '../../../core';
import {
  DepositParams,
  VotingParams,
  TallyParams,
} from '../../../core/gov/params';

export interface GovParams {
  deposit_params: DepositParams;
  voting_params: VotingParams;
  tally_params: TallyParams;
}

export namespace GovParams {
  export interface Data {
    deposit_params: DepositParams.Data;
    voting_params: VotingParams.Data;
    tally_params: TallyParams.Data;
  }
}

export interface Tally {
  yes: Int;
  no: Int;
  abstain: Int;
  no_with_veto: Int;
}

export namespace Tally {
  export interface Data {
    yes: string;
    no: string;
    abstain: string;
    no_with_veto: string;
  }
}

export class GovAPI extends BaseAPI {
  public async proposals(): Promise<Proposal[]> {
    return this.c
      .get<Proposal.Data[]>(`/gov/proposals`)
      .then(d => d.result.map(Proposal.fromData));
  }

  public async proposal(proposalId: number): Promise<Proposal> {
    return this.c
      .get<Proposal.Data>(`/gov/proposals/${proposalId}`)
      .then(d => Proposal.fromData(d.result));
  }

  public async proposer(proposalId: number): Promise<AccAddress> {
    return this.c
      .get<{ proposer: AccAddress }>(`/gov/proposals/${proposalId}/proposer`)
      .then(d => d.result.proposer);
  }

  public async deposits(proposalId: number): Promise<any> {
    return this.c
      .get(`/gov/proposals/${proposalId}/deposits`)
      .then(d => d.result);
  }

  public async votes(proposalId: number): Promise<any> {
    return this.c
      .get(`/gov/proposals/${proposalId}/deposits`)
      .then(d => d.result);
  }

  public async tally(proposalId: number): Promise<Tally> {
    return this.c
      .get<Tally.Data>(`/gov/proposals/${proposalId}/tally`)
      .then(d => d.result)
      .then(d => ({
        yes: new Int(d.yes),
        no: new Int(d.no),
        no_with_veto: new Int(d.no_with_veto),
        abstain: new Int(d.abstain),
      }));
  }

  public async depositParameters(): Promise<DepositParams> {
    return this.c
      .get<DepositParams.Data>(`/gov/parameters/deposit`)
      .then(d => d.result)
      .then(d => ({
        max_deposit_period: Number.parseInt(d.max_deposit_period),
        min_deposit: Coins.fromData(d.min_deposit),
      }));
  }

  public async votingParameters(): Promise<VotingParams> {
    return this.c
      .get<VotingParams.Data>(`/gov/parameters/voting`)
      .then(d => d.result)
      .then(d => ({ voting_period: Number.parseInt(d.voting_period) }));
  }

  public async tallyParameters(): Promise<TallyParams> {
    return this.c
      .get<TallyParams.Data>(`/gov/parameters/tallying`)
      .then(d => d.result)
      .then(d => ({
        quorum: new Dec(d.quorum),
        veto: new Dec(d.veto),
        threshold: new Dec(d.threshold),
      }));
  }

  public async parameters(): Promise<GovParams> {
    const [deposit_params, voting_params, tally_params] = await Promise.all([
      this.depositParameters(),
      this.votingParameters(),
      this.tallyParameters(),
    ]);
    return {
      deposit_params,
      voting_params,
      tally_params,
    };
  }
}
